import { Chart as ChartJS, Chart, ChartData, ChartOptions } from "chart.js";
import "chartjs-adapter-date-fns";
import { downloadDiagram } from "../../../../../../../utils/download-diagram";
import { IChartData } from "../interfaces";
import { buildChartLegendLabels } from "./multiline-chart/chart-legend-labels";

export type TChartExportFormat = "png" | "jpg";

export type TChartExportHandler = (format: TChartExportFormat) => void;

export interface IChartExportControlProps {
  registerExport?: (handler: TChartExportHandler | null) => void;
}

export const getChartExportFileName = (format: TChartExportFormat): string => {
  const date = new Date().toISOString().split("T")[0];
  return `scheme-train-chart-report-${date}.${format}`;
};

interface IExportCanvas {
  chart: ChartJS<"line">;
  canvas: HTMLCanvasElement;
}

export type TChartExportCanvasFactory = () => IExportCanvas;

export const createExportCanvas = (
  chartData: ChartData<"line", any[]>,
  options: ChartOptions<"line"> & { plugins: any },
  data: IChartData[],
  width: number,
  height: number,
  colorBySeries?: Map<string, string>,
): IExportCanvas => {
  const dpr = window.devicePixelRatio || 1;

  const canvas = document.createElement("canvas");
  canvas.width = width / dpr;
  canvas.height = height / dpr;

  const chart = new ChartJS(canvas, {
    type: "line",
    data: chartData,
    options: {
      ...options,
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        ...options.plugins,
        title: {
          ...options.plugins.title,
          display: true,
        },
        legend: {
          ...options.plugins.legend,
          display: true,
          position: "bottom",
          labels: {
            ...options.plugins.legend.labels,
            generateLabels: (chart: Chart<"line">) =>
              buildChartLegendLabels(chart, data, colorBySeries, { exportMode: true }),
          },
          htmlLegend: undefined,
        },
      },
    },
  });

  return {
    chart,
    canvas,
  };
};

export const exportChart = (exportItem: IExportCanvas, format: "image/png" | "image/jpeg", fileName: string) => {
  const { chart, canvas } = exportItem;

  let exportCanvas: HTMLCanvasElement = canvas;

  try {
    if (format === "image/jpeg") {
      const jpgCanvas = document.createElement("canvas");
      jpgCanvas.width = canvas.width;
      jpgCanvas.height = canvas.height;

      const ctx = jpgCanvas.getContext("2d");

      if (ctx) {
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, jpgCanvas.width, jpgCanvas.height);
        ctx.drawImage(canvas, 0, 0);

        exportCanvas = jpgCanvas;
      }
    }

    downloadDiagram(exportCanvas.toDataURL(format, 1), fileName);
  } finally {
    chart.destroy();
    canvas.remove();

    if (exportCanvas !== canvas) {
      exportCanvas.remove();
    }
  }
};

export const exportChartGroup = (
  exportItems: IExportCanvas[],
  format: "image/png" | "image/jpeg",
  fileName: string,
) => {
  if (!exportItems.length) {
    return;
  }

  const gap = 24;

  try {
    const totalHeight = exportItems.reduce((sum, item) => sum + item.canvas.height, 0) + gap * (exportItems.length - 1);

    const resultCanvas = document.createElement("canvas");
    resultCanvas.width = exportItems[0].canvas.width;
    resultCanvas.height = totalHeight;

    const ctx = resultCanvas.getContext("2d");

    if (!ctx) {
      return;
    }

    if (format === "image/jpeg") {
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, resultCanvas.width, resultCanvas.height);
    }

    let y = 0;

    for (const item of exportItems) {
      ctx.drawImage(item.canvas, 0, y);
      y += item.canvas.height + gap;
    }

    downloadDiagram(resultCanvas.toDataURL(format, 1), fileName);

    resultCanvas.remove();
  } finally {
    exportItems.forEach(({ chart, canvas }) => {
      chart.destroy();
      canvas.remove();
    });
  }
};
